import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { customersApi } from '../../services/api';
import { formatCurrency, formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-800 font-manrope">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} className="text-slate-500" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '', whatsapp_opt_out: false });

  const { data, isLoading } = useQuery({
    queryKey: ['customers', search, page],
    queryFn: () => customersApi.list({ search, limit: 20, page }).then(r => r.data),
  });

  const mutation = useMutation({
    mutationFn: d => modal?.mode === 'create' ? customersApi.create(d) : customersApi.update(modal.id, d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['customers'] }); setModal(null); toast.success(t('Pelanggan disimpan', 'Customer saved')); },
    onError: err => toast.error(formatApiError(err)),
  });

  const handleDelete = async (id) => {
    if (!window.confirm(t('Hapus pelanggan?', 'Delete customer?'))) return;
    await customersApi.remove(id);
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    toast.success(t('Dihapus', 'Deleted'));
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const openCreate = () => { setModal({ mode: 'create' }); setForm({ name: '', phone: '', email: '', address: '', notes: '', whatsapp_opt_out: false }); };
  const openEdit = (c) => { setModal({ mode: 'edit', id: c.id }); setForm({ ...c, whatsapp_opt_out: Boolean(c.whatsapp_opt_out) }); };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Pelanggan', 'Customers')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{data?.total || 0} {t('total pelanggan', 'total customers')}</p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={18} />{t('Tambah Pelanggan', 'Add Customer')}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); }} className="p-4 border-b border-slate-50">
            <div className="relative max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                placeholder={t('Cari nama/telepon...', 'Search name/phone...')}
                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
            </div>
          </form>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {[t('Nama', 'Name'), t('Telepon', 'Phone'), 'Email', 'WhatsApp', t('Total Pesanan', 'Orders'), t('Total Belanja', 'Spent'), ''].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? [1,2,3].map(i => <tr key={i}>{[1,2,3,4,5,6,7].map(j => <td key={j} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>) :
                  (data?.items?.length === 0) ? <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400 text-sm">{t('Belum ada pelanggan', 'No customers')}</td></tr> :
                  (data?.items || []).map(c => (
                    <tr key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)} data-testid={`customer-row-${c.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors">
                      
                     <td className="px-5 py-3.5">
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
      <span className="text-[#0f6c44] text-xs font-bold">{c.name?.charAt(0)}</span>
    </div>
    <span className="text-sm font-medium text-slate-800 hover:text-[#0f6c44] transition-colors">{c.name}</span>
  </div>
</td>


                      
                      <td className="px-5 py-3.5 text-sm text-slate-600">{c.phone || '-'}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{c.email || '-'}</td>
                      <td className="px-5 py-3.5">{c.whatsapp_opt_out ? <span data-testid={`customer-wa-disabled-${c.id}`} className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">WA Nonaktif</span> : <span data-testid={`customer-wa-enabled-${c.id}`} className="inline-flex rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">WA Aktif</span>}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{c.total_orders || 0}x</td>
                      <td className="px-5 py-3.5 text-sm font-medium text-slate-800">{formatCurrency(c.total_spent || 0)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-1 justify-end">
                          <button onClick={(event) => { event.stopPropagation(); openEdit(c); }} data-testid={`edit-customer-${c.id}`} className="p-2 text-slate-400 hover:text-[#0f6c44] hover:bg-emerald-50 rounded-lg transition-colors"><Edit2 size={15} /></button>
                          <button onClick={(event) => { event.stopPropagation(); handleDelete(c.id); }} data-testid={`delete-customer-${c.id}`} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal && (
        <Modal title={modal.mode === 'create' ? t('Tambah Pelanggan', 'Add Customer') : t('Edit Pelanggan', 'Edit Customer')} onClose={() => setModal(null)}>
          <div className="space-y-4">
            {[
              { k: 'name', label: t('Nama', 'Name'), required: true },
              { k: 'phone', label: t('No. Telepon', 'Phone'), type: 'tel' },
              { k: 'email', label: 'Email', type: 'email' },
              { k: 'address', label: t('Alamat', 'Address') },
            ].map(f => (
              <div key={f.k}>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}{f.required ? ' *' : ''}</label>
                <input type={f.type || 'text'} value={form[f.k] || ''} onChange={set(f.k)} required={f.required}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
              </div>
            ))}
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <input type="checkbox" checked={!form.whatsapp_opt_out} onChange={event => setForm(current => ({ ...current, whatsapp_opt_out: !event.target.checked }))} data-testid="customer-whatsapp-notifications-toggle" className="mt-0.5 h-4 w-4 accent-[#0f6c44]" />
              <span><span className="block font-semibold">Kirim notifikasi WhatsApp</span><span className="mt-0.5 block text-xs text-slate-500">Matikan bila pelanggan tidak ingin menerima pesan proaktif.</span></span>
            </label>
            <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending || !form.name}
              data-testid="save-customer-button"
              className="w-full h-11 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] transition-all flex items-center justify-center disabled:opacity-70">
              {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : t('Simpan', 'Save')}
            </button>
          </div>
        </Modal>
      )}

      {/* Pagination */}
      {(data?.total || 0) > 20 && (
        <div className="flex items-center justify-between py-3">
          <p className="text-sm text-slate-500">
            {t('Halaman', 'Page')} {page} · {data?.total || 0} {t('pelanggan', 'customers')}
          </p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40 transition-colors">
              {t('Sebelumnya', 'Prev')}
            </button>
            <button onClick={() => setPage(p => p + 1)} disabled={(page * 20) >= (data?.total || 0)}
              className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40 transition-colors">
              {t('Berikutnya', 'Next')}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
